import React, { useEffect, useRef, useState } from "react";
import Icon from "./Icon";
import { STATUS } from "./catalogModel";

export function StatusChip({ status }) {
  const s = STATUS[status];
  return <span className={`ac-chip ${status}`}><Icon n={s.icon} s={12} w={2.4} />{s.label}</span>;
}

/** Accessible on/off switch. A disabled switch stays focusable and explains itself in a tooltip. */
export function Switch({ on, disabled, onChange, label, tip }) {
  return (
    <span className="ac-tipwrap" data-tip={disabled ? tip : undefined}>
      <button type="button" role="switch" aria-checked={on} aria-label={label} aria-disabled={disabled || undefined}
        className={`ac-sw${on ? " on" : ""}${disabled ? " dis" : ""}`}
        onClick={() => { if (!disabled) onChange(!on); }}><i /></button>
    </span>
  );
}

/** Arrow-key navigation, Escape/Tab to close and click-outside dismissal for a popup menu. */
function useMenuKeys(open, setOpen, ref) {
  useEffect(() => {
    const menu = ref.current;
    if (!open || !menu) return undefined;
    const items = () => [...menu.querySelectorAll("[role=menuitem],[role=menuitemcheckbox]")].filter((b) => !b.disabled);
    const first = items()[0];
    if (first) first.focus();
    const onKey = (e) => {
      const list = items();
      const i = list.indexOf(document.activeElement);
      if (e.key === "ArrowDown") { e.preventDefault(); list[(i + 1) % list.length].focus(); }
      if (e.key === "ArrowUp") { e.preventDefault(); list[(i - 1 + list.length) % list.length].focus(); }
      if (e.key === "Escape" || e.key === "Tab") setOpen(false);
    };
    const onDown = (e) => { if (!menu.parentNode.contains(e.target)) setOpen(false); };
    menu.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => { menu.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onDown); };
  }, [open, setOpen, ref]);
}

/** Kebab menu. Items are { icon, label, danger, onSelect } or the string "-" for a divider. */
export function RowMenu({ items, label }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useMenuKeys(open, setOpen, ref);
  return (
    <span className="ac-menuwrap">
      <button type="button" className="ac-ib" aria-haspopup="menu" aria-expanded={open} aria-label={label} onClick={() => setOpen(!open)}>
        <Icon n="more" s={16} />
      </button>
      {open && (
        <div className="ac-menu" role="menu" ref={ref}>
          {items.map((it, i) => (it === "-" ? <hr key={`d${i}`} /> : (
            <button key={it.label} role="menuitem" type="button" className={it.danger ? "danger" : ""}
              onClick={() => { setOpen(false); if (it.onSelect) it.onSelect(); }}>
              <Icon n={it.icon} s={15} /><span>{it.label}</span>
            </button>
          )))}
        </div>
      )}
    </span>
  );
}

/** Multi-select filter dropdown; `render` maps an option to its display label. */
export function FilterMenu({ label, options, value, onChange, render = (o) => o }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useMenuKeys(open, setOpen, ref);
  const toggle = (o) => onChange(value.includes(o) ? value.filter((x) => x !== o) : [...value, o]);
  return (
    <span className="ac-menuwrap">
      <button type="button" className={`ac-fbtn${value.length ? " set" : ""}`} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}>
        {label}{value.length > 0 && <b>{value.length}</b>}<Icon n="chevDown" s={14} />
      </button>
      {open && (
        <div className="ac-menu left" role="menu" aria-label={`${label} filter`} ref={ref}>
          {options.map((o) => (
            <button key={o} role="menuitemcheckbox" aria-checked={value.includes(o)} type="button" onClick={() => toggle(o)}>
              <span className={`ac-cbx${value.includes(o) ? " on" : ""}`}>{value.includes(o) && <Icon n="check" s={11} w={3} />}</span>
              <span>{render(o)}</span>
            </button>
          ))}
        </div>
      )}
    </span>
  );
}
